<template>
  <div class="allerta-caricamenti">
    <div class="header-section">
      <h2 class="page-title">Allerta Caricamenti</h2>
      <p class="subtitle">Monitoraggio anomalie e stati dei caricamenti</p>
    </div>

    <Loader v-if="loading" />

    <div v-else class="content-section">
      <div class="stats-grid">
        <div v-for="(anomalia, index) in anomalie" :key="index" class="stat-card"
          :class="getCardClass(anomalia.data.length)">
          <div class="stat-number">{{ anomalia.data.length }}</div>
          <div class="stat-label">{{ anomalia.msg }}</div>
        </div>
      </div>

      <div class="tables-section">
        <div v-for="(anomalia, index) in anomalie" :key="index" v-show="anomalia.data.length > 0"
          class="table-container">
          <div class="table-header">
            <h3 class="table-title">
              {{ anomalia.msg }}
              <Badge :value="anomalia.data.length" severity="info" />
            </h3>
          </div>

          <div class="table-wrapper">
            <DataTable :value="anomalia.data" :paginator="true" :rows="10" :rowsPerPageOptions="[10, 25, 50]"
              responsiveLayout="scroll"
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate="Mostra {first} - {last} di {totalRecords} record" class="p-datatable-sm">
              <Column v-for="(heading, colIndex) in anomalia.headings" :key="colIndex" :field="getFieldKey(heading)"
                :header="heading" :sortable="true">
                <template #body="slotProps">
                  <span v-if="heading.includes('giorni')" class="days-badge"
                    :class="getDaysClass(slotProps.data[getFieldKey(heading)])">
                    {{ slotProps.data[getFieldKey(heading)] }}
                  </span>
                  <span v-else-if="heading === 'id caricamento' || heading === 'id_matrice'" class="id-badge">
                    {{ slotProps.data[getFieldKey(heading)] }}
                  </span>
                  <span v-else>
                    {{ slotProps.data[getFieldKey(heading)] }}
                  </span>
                </template>
              </Column>
            </DataTable>
          </div>
        </div>
      </div>
    </div>

    <Toast />
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useToast } from 'primevue/usetoast';
import axios from 'axios';
import Loader from "@/components/Loader.vue"

export default {
  name: 'AllertaCaricamenti',
  components: {
    Loader
  },
  setup() {
    const toast = useToast();
    const anomalie = ref([]);
    const loading = ref(false);
    const error = ref(null);

    const apiBaseUrl = process.env.VUE_APP_ASK_PRATICHE_URL;

    const fetchAnomalieData = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await axios.get(`${apiBaseUrl}/api/allerta-caricamenti`);
        anomalie.value = response.data;

        toast.add({
          severity: 'success',
          summary: 'Dati Caricati',
          detail: 'Anomalie caricate con successo',
          life: 3000
        });
      } catch (err) {
        console.error('Errore nel recupero delle anomalie:', err);
        error.value = 'Impossibile caricare i dati. Riprova più tardi.';

        toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare le anomalie dal database',
          life: 3000
        });
      } finally {
        loading.value = false;
      }
    };


    const getFieldKey = (heading) => {
      const fieldMapping = {
        'id caricamento': 'id',
        'data caricamento': 'data_caricamento',
        'data_agiornamento': 'data_agiornamento',
        'agente firmatario': 'agente_firmatario',
        'agente master': 'agente_master',
        'stato caricamento': 'stato_caricamento',
        'giorni in attesa firma': 'giorni in attesa firma',
        'giorni in attesa gestione da blocco': 'giorni in attesa gestione da blocco',
        'fornitura': 'pp',
        'giorni in attesa validazione': 'giorni in attesa validazione',
        'giorni in attesa precheck': 'giorni in attesa precheck',
        'data ultimo precheck': 'data_ultimo_precheck',
        'giorni in blocco precheck anomalo': 'giorni',
        'giorni in attesa gestione pratica': 'giorni',
        'giorni in attesa aggiornamento': 'giorni in attesa aggiornamento',
        'stato switch': 'stato_switch',
        'giorni in attesa switch': 'giorni in attesa switch',
        'giorni in attesa timoe/timg': 'giorni in attesa timoe/timg'
      };

      return fieldMapping[heading] || heading;
    };


    const getDaysClass = (days) => {
      if (typeof days !== 'number') return '';

      if (days <= 3) return 'days-good';
      if (days <= 7) return 'days-warning';
      return 'days-danger';
    };

    const getCardClass = (count) => {
      if (count === 0) return 'stat-card-success';
      if (count <= 10) return 'stat-card-warning';
      return 'stat-card-danger';
    };


    const convertToCSV = (anomalia) => {
      const headers = anomalia.headings.join(',');
      const rows = anomalia.data.map(row =>
        anomalia.headings.map(heading => `"${row[heading] || ''}"`).join(',')
      ).join('\n');

      return `${headers}\n${rows}`;
    };

    onMounted(() => {
      fetchAnomalieData();
    });

    return {
      anomalie,
      loading,
      error,
      fetchAnomalieData,
      getFieldKey,
      getDaysClass,
      getCardClass,
    };
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap');

.allerta-caricamenti {
  font-family: 'Montserrat', sans-serif;
  padding: 1.5rem;
}

.header-section {
  margin-bottom: 2rem;
}

.page-title {
  font-size: 1.8rem;
  color: #333;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.subtitle {
  color: #666;
  font-size: 1rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem;
  color: #666;
  gap: 1rem;
}

.error-container {
  margin: 2rem 0;
}

.content-section {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.stat-card {
  background: linear-gradient(135deg, #05b4a5 0%, #215159 100%);
  color: white;
  padding: 1.5rem;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-card-success {
  background: linear-gradient(135deg, #05b4a5 0%, #04a394 100%);
}

.stat-card-warning {
  background: linear-gradient(135deg, #05b4a5 0%, #215159 100%);
}

.stat-card-danger {
  background: linear-gradient(135deg, #215159 0%, #1a4148 100%);
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.stat-label {
  font-size: 0.9rem;
  opacity: 0.95;
  font-weight: 500;
}

.tables-section {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.table-header {
  background: #2c3e50;
  color: white;
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* border-bottom: 1px solid #dee2e6; */
}

.table-title {
  margin: 0;
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.table-wrapper {
  padding: 0;
}

.days-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  min-width: 30px;
  color: rgb(0, 0, 0);
}

.days-good, .days-warning, .days-danger {
  background: none !important;
  color: rgb(0, 0, 0);
  font-weight:700;
}


.id-badge {
  background: #05b4a5;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  font-weight: 600;
}

@media (max-width: 768px) {
  .allerta-caricamenti {
    padding: 1rem;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .table-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .stat-card {
    padding: 1rem;
  }

  .stat-number {
    font-size: 2rem;
  }
}

:deep(.stat-card-danger) {
  background: #2c3e50 !important;
}

:deep(.id-badge) {
  background: none !important;
  color: black;
  font-weight: 700;
  font-size: 0.9rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
}

:deep(.days-danger) {
  background: none !important;
  color: black;
  font-weight: 600;
  font-size: 0.9rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
}

:deep(.p-datatable) {
  background: transparent;
  overflow: hidden;
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
  background: #9fadbd;
  color: white;
  border: none;
  font-weight: 600;
  padding: 1rem 0.75rem;
  font-size: 0.9rem;
}

:deep(.p-datatable .p-datatable-tbody > tr) {
  background: white;
  border: none;
  color: black !important;
}

:deep(.p-datatable .p-sortable-column:not(.p-highlight):hover) {
  background-color: #2c3e50;
  color: white;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
  border: none;
  border-bottom: 1px solid #f0f0f0;
  padding: 0.75rem;
  color: #2c3e50;
  font-weight: 500;
}

:deep(.p-datatable .p-datatable-tbody > tr:hover) {
  background: rgba(5, 180, 165, 0.1);
}

:deep(.p-datatable .p-datatable-tbody > tr:nth-child(even)) {
  background: #f8f9fa;
}

:deep(.p-datatable .p-datatable-tbody > tr:nth-child(even):hover) {
  background: rgba(5, 180, 165, 0.15);
}

:deep(.p-paginator) {
  background: #f8f9fa;
  border: none;
  border-top: 1px solid #dee2e6;
  padding: 1rem;
}

:deep(.p-paginator .p-paginator-pages .p-paginator-page.p-highlight) {
  background: #2c3e50;
  border-color: #05b4a5;
  color: white;
}

:deep(.p-paginator .p-paginator-pages .p-paginator-page:hover) {
  background: rgba(5, 180, 165, 0.1);
  border-color: #05b4a5;
}
</style>